import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { AlertTriangle, Camera, Pause, Play, RefreshCw, Settings2, ZoomIn, ZoomOut } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/app/components/ui/Button'
import { StepShell } from '@/app/components/ui/StepShell'
import { PhotoTile } from '@/app/components/PhotoTile'
import { PhotoPreview } from '@/app/components/PhotoPreview'
import { PhotoContextMenu, type ContextMenuTarget } from '@/app/components/PhotoContextMenu'
import { useThumb } from '@/app/lib/useThumb'
import { missingRequirements } from '@/app/lib/capabilities'
import {
  DEFAULT_SETTINGS,
  GALLERY_TILE_MAX,
  GALLERY_TILE_MIN,
  GALLERY_TILE_STEP,
} from '@/app/lib/local/settings'
import { useAppStore } from '@/app/store/useAppStore'
import { useFlowStore } from '@/app/store/useFlowStore'
import type { ImageFile } from '@/core/types'

/**
 * Step 1 — choose the photos.
 *
 * The desktop app made the operator press Refresh to see anything: new files
 * only raised a counter, and the gallery rebuilt itself from scratch on demand.
 * Here photos arrive on their own and Refresh is a fallback, not the mechanism.
 *
 * Selection order is preserved and shown on each tile, because it decides which
 * layout slot each photo lands in — the compositor pairs photo *i* with slot
 * *i*. That was invisible in the old UI and is the single most common way to
 * get a print you did not expect.
 *
 * Two ways to look closer, because picking from a contact sheet at thumbnail
 * size is guesswork: zoom changes the density of the whole grid, and a preview
 * opens one photo at its real resolution.
 */
export function SelectStep({
  onNext,
  active = true,
}: {
  onNext: () => void
  /**
   * False while a later step or the finish screen is showing. FlowScreen keeps
   * this step mounted and hidden rather than unmounting it (see there), so it
   * has to be told when it is not the screen the operator is looking at.
   */
  active?: boolean
}) {
  const navigate = useNavigate()
  const {
    files,
    watching,
    pendingCount,
    capabilities,
    settings,
    updateSettings,
    toggleWatch,
    refreshGallery,
  } = useAppStore()
  const { selected, toggleSelect, clearSelection } = useFlowStore()

  /**
   * Nothing in step 1 runs until the console is completely set up.
   *
   * Both controls are gated, not just the watcher: "Muat ulang" reads the same
   * folder handle and would fail the same way, and leaving one live implies the
   * other is broken rather than that the setup is incomplete. The list is
   * rendered where the buttons are — an operator who has just found a dead
   * button should not have to read the banner to work out why.
   */
  const missing = capabilities
    ? missingRequirements(capabilities, settings?.videoEnabled ?? true)
    : []
  const setUp = capabilities !== null && missing.length === 0

  /** Which photo the modal is showing, by identifier. */
  const [preview, setPreview] = useState<string | null>(null)
  const [menu, setMenu] = useState<ContextMenuTarget | null>(null)

  const order = useMemo(() => {
    const map = new Map<string, number>()
    selected.forEach((id, i) => map.set(id, i + 1))
    return map
  }, [selected])

  const tileSize = settings?.galleryTileSize ?? DEFAULT_SETTINGS.galleryTileSize

  const zoom = (delta: number) => {
    const next = Math.max(GALLERY_TILE_MIN, Math.min(GALLERY_TILE_MAX, tileSize + delta))
    if (next === tileSize) return
    void updateSettings({ galleryTileSize: next })
  }

  const previewIndex = preview === null ? -1 : files.findIndex((f) => f.identifier === preview)
  const previewFile = previewIndex >= 0 ? files[previewIndex] : null
  // Arrows walk the gallery in the order it is shown, so the preview and the
  // grid agree about what "the next photo" is.
  const prevFile = previewIndex > 0 ? files[previewIndex - 1] : undefined
  const nextFile = previewIndex >= 0 ? files[previewIndex + 1] : undefined

  // The gallery reloads under the operator while a preview is open — the camera
  // keeps saving, and a file can also be moved away. Close rather than leave a
  // modal pinned to a photo that is no longer in the list.
  useEffect(() => {
    if (preview !== null && previewIndex < 0) setPreview(null)
  }, [preview, previewIndex])

  // Both overlays render into <body>, so hiding this step does not hide them.
  // Leaving the step must close them, or a preview opened here would sit on top
  // of the layout step.
  useEffect(() => {
    if (active) return
    setPreview(null)
    setMenu(null)
  }, [active])

  useEffect(() => {
    // A running watcher is already polling every 3s, and a poll is a serial
    // walk of the whole folder that opens every file — 183ms and one open per
    // photo on a 120-frame folder. Doing one more on mount bought nothing:
    // anything that landed while this step was away raised `pendingCount`, and
    // the effect below refreshes on that.
    if (useAppStore.getState().watching) return
    refreshGallery().catch(() => {
      /* the disconnected banner already explains this */
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // New photos land automatically; the operator should never have to ask.
  useEffect(() => {
    if (pendingCount > 0) {
      refreshGallery().catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount])

  const guard = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : message)
    }
  }

  const menuFile = menu ? files.find((f) => f.identifier === menu.identifier) : undefined

  return (
    <StepShell
      title="Pilih foto"
      hint="Urutan pilih menentukan posisi di layout."
      onNext={onNext}
      nextDisabled={selected.length === 0}
      status={
        selected.length > 0 ? (
          <span className="inline-flex items-center gap-3">
            <strong className="text-ink">{selected.length} dipilih</strong>
            <button
              type="button"
              onClick={clearSelection}
              className="min-h-[44px] text-ink-muted underline underline-offset-4"
            >
              Hapus pilihan
            </button>
          </span>
        ) : (
          `${files.length} foto`
        )
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant={watching ? 'secondary' : 'primary'}
          onClick={() => guard(toggleWatch, 'Tidak bisa mengubah pemantauan')}
          disabled={!setUp}
        >
          {watching ? <Pause size={18} /> : <Play size={18} />}
          {watching ? 'Jeda' : 'Mulai pantau'}
        </Button>

        <Button
          variant="secondary"
          onClick={() => guard(refreshGallery, 'Tidak bisa memuat galeri')}
          disabled={!setUp}
        >
          <RefreshCw size={18} />
          Muat ulang
        </Button>

        {watching && (
          <span className="ml-1 inline-flex items-center gap-2 text-[13px] text-brand">
            <span className="citaria-live h-2 w-2 rounded-full bg-brand" />
            Memantau folder kamera
          </span>
        )}

        {/*
          Zoom sits at the far end, away from the two controls that change what
          is in the gallery. It only changes how the same photos are drawn.
        */}
        <div className="ml-auto inline-flex items-center gap-1">
          <Button
            variant="secondary"
            aria-label="Perkecil tampilan"
            title="Perkecil tampilan"
            onClick={() => zoom(-GALLERY_TILE_STEP)}
            disabled={tileSize <= GALLERY_TILE_MIN}
            className="w-[48px] px-0"
          >
            <ZoomOut size={18} />
          </Button>
          <Button
            variant="secondary"
            aria-label="Perbesar tampilan"
            title="Perbesar tampilan"
            onClick={() => zoom(GALLERY_TILE_STEP)}
            disabled={tileSize >= GALLERY_TILE_MAX}
            className="w-[48px] px-0"
          >
            <ZoomIn size={18} />
          </Button>
        </div>
      </div>

      {!setUp ? (
        <NotSetUp missing={missing} onOpenSettings={() => navigate('/settings')} />
      ) : files.length === 0 ? (
        <EmptyState watching={watching} />
      ) : (
        <div
          className="grid gap-3"
          // Driven by the zoom control. `auto-fill` keeps every row full at any
          // size, so the operator is choosing density, not a column count.
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${tileSize}px, 1fr))` }}
        >
          {files.map((file) => (
            <GalleryTile
              key={file.identifier}
              file={file}
              selected={order.has(file.identifier)}
              order={order.get(file.identifier)}
              onToggle={() => toggleSelect(file.identifier)}
              onPreview={() => setPreview(file.identifier)}
              onContextMenu={(event) => {
                event.preventDefault()
                setMenu({ identifier: file.identifier, x: event.clientX, y: event.clientY })
              }}
            />
          ))}
        </div>
      )}

      {menu && menuFile && (
        <PhotoContextMenu
          target={menu}
          selected={order.has(menu.identifier)}
          onPreview={() => {
            setPreview(menu.identifier)
            setMenu(null)
          }}
          onToggleSelect={() => {
            toggleSelect(menu.identifier)
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      )}

      {previewFile && (
        <PhotoPreview
          file={previewFile}
          selected={order.has(previewFile.identifier)}
          order={order.get(previewFile.identifier)}
          onToggleSelect={() => toggleSelect(previewFile.identifier)}
          onPrev={prevFile ? () => setPreview(prevFile.identifier) : undefined}
          onNext={nextFile ? () => setPreview(nextFile.identifier) : undefined}
          onClose={() => setPreview(null)}
        />
      )}
    </StepShell>
  )
}

/**
 * What is standing between the operator and a working console.
 *
 * This is what is left of the setup screen, and it is deliberately less than
 * that screen was: it names only what is actually missing, it does not cover
 * the console, and it sits directly under the buttons it explains. An operator
 * mid-event who has just had a folder permission lapse sees one line and a way
 * to fix it rather than a six-row checklist of things that are already fine.
 */
function NotSetUp({
  missing,
  onOpenSettings,
}: {
  missing: { key: string; label: string; detail: string }[]
  onOpenSettings: () => void
}) {
  return (
    <div className="grid place-items-center rounded-[16px] border border-dashed border-warn/40 bg-warn/5 py-16 text-center">
      <Settings2 size={40} className="mb-4 text-warn" />
      <p className="text-[17px] font-medium">Pengaturan belum lengkap</p>
      <p className="mt-1 max-w-[420px] text-[14px] text-ink-muted">
        Semua ini harus siap sebelum acara dimulai. Pemantauan folder baru bisa dijalankan
        setelahnya.
      </p>

      <ul className="mt-5 w-full max-w-[420px] space-y-2 text-left">
        {missing.map((requirement) => (
          <li
            key={requirement.key}
            className="flex items-start gap-3 rounded-[12px] border border-line px-4 py-3"
          >
            <span className="mt-[2px] shrink-0 text-warn">
              <AlertTriangle size={16} />
            </span>
            <span>
              <span className="block text-[14px] font-bold text-ink">{requirement.label}</span>
              <span className="block text-[12px] text-ink-muted">{requirement.detail}</span>
            </span>
          </li>
        ))}
      </ul>

      <Button className="mt-6 px-5" onClick={onOpenSettings}>
        <Settings2 size={18} />
        Buka Pengaturan
      </Button>
    </div>
  )
}

function EmptyState({ watching }: { watching: boolean }) {
  return (
    <div className="grid place-items-center rounded-[16px] border border-dashed border-line py-20 text-center">
      <Camera size={40} className="mb-4 text-ink-faint" />
      <p className="text-[17px] font-medium">Belum ada foto</p>
      <p className="mt-1 max-w-[380px] text-[14px] text-ink-muted">
        {watching
          ? 'Memantau folder kamera. Foto akan muncul sendiri begitu kamera menyimpannya.'
          : 'Tekan “Mulai pantau” agar foto dari kamera muncul di sini.'}
      </p>
    </div>
  )
}

/**
 * One tile, with its thumbnail resolved locally.
 *
 * Split out so the hook runs per photo: the agent build could name a thumbnail
 * URL synchronously, but rendering one is now this tab's own work and each tile
 * has to wait for its own.
 */
function GalleryTile({
  file,
  selected,
  order,
  onToggle,
  onPreview,
  onContextMenu,
}: {
  file: ImageFile
  selected: boolean
  order?: number
  onToggle: () => void
  onPreview: () => void
  onContextMenu: (event: React.MouseEvent) => void
}) {
  const src = useThumb(file, 400)

  return (
    <PhotoTile
      src={src}
      alt={file.fileName}
      selected={selected}
      order={order}
      onToggle={onToggle}
      onPreview={onPreview}
      onContextMenu={onContextMenu}
    />
  )
}
